/**
 * Schedule-module API helpers (groups, blocks, weather).
 *
 * `getScheduleBlocks` is DEPRECATED; prefer `fetchAllScheduleBlocksInRange`
 * for Schedule page loads so pagination is fully consumed.
 */

import { apiRequest } from "./axios";
import { API_PATHS, API_ENDPOINTS } from "../../config";
import {
  getPlanningWeatherInRange,
  loadPlanningWeatherSplitByDay,
  patchPlanningScopeBlock,
} from "./planning";

/**
 * Schedule list endpoints use DRF `LimitOffsetPagination` (project `REST_FRAMEWORK` default).
 * Response shape: `{ count, next, previous, results: [...] }`.
 * @param {*} data Parsed JSON body from GET list
 * @returns {Array}
 */
export function scheduleListResults(data) {
  if (data == null || !Array.isArray(data.results)) {
    return [];
  }
  return data.results;
}

/** Page size for schedule list requests (blocks may require multiple pages per window). */
const SCHEDULE_LIST_LIMIT = 500;

/**
 * Append `limit` for a single-page schedule list GET.
 * @param {string} path API path (may already include query string).
 * @returns {string}
 */
function scheduleListUrl(path) {
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}limit=${SCHEDULE_LIST_LIMIT}`;
}

/**
 * Normalize a DRF `next` URL to a path relative to `API_ENDPOINTS.BASE`.
 *
 * @param {string|null|undefined} nextUrl
 * @returns {string|null}
 */
function scheduleApiPathFromNext(nextUrl) {
  if (!nextUrl) {
    return null;
  }
  const base = API_ENDPOINTS.BASE.replace(/\/$/, "");
  if (nextUrl.startsWith(base)) {
    return nextUrl.slice(base.length);
  }
  try {
    const parsed = new URL(nextUrl);
    const path = `${parsed.pathname}${parsed.search}`;
    if (path.startsWith("/api/")) {
      return path.replace(/^\/api/, "");
    }
    return path;
  } catch {
    return null;
  }
}

/**
 * Build blocks list URL for an inclusive date window.
 *
 * @param {string} startKey YYYY-MM-DD
 * @param {string} endKey YYYY-MM-DD
 * @returns {string}
 */
function scheduleBlocksRangePath(startKey, endKey) {
  const params = new URLSearchParams({
    start: startKey,
    end: endKey,
  });
  return `${API_PATHS.SCHEDULE.BLOCKS}?${params.toString()}`;
}

/**
 * Paginated schedule groups (`{ count, next, previous, results }`).
 * @returns {Promise<{ count: number, next: string|null, previous: string|null, results: Array<Object> }>}
 */
export async function getScheduleGroups() {
  return apiRequest(scheduleListUrl(API_PATHS.SCHEDULE.GROUPS), null, {
    method: "GET",
  });
}

/**
 * One page of blocks overlapping `startKey`–`endKey` with aspects trimmed to that range.
 *
 * @param {string} startKey YYYY-MM-DD inclusive
 * @param {string} endKey YYYY-MM-DD inclusive
 * @returns {Promise<{ count: number, next: string|null, previous: string|null, results: Array<Object> }>}
 */
export async function getScheduleBlocksInRange(startKey, endKey) {
  return apiRequest(scheduleListUrl(scheduleBlocksRangePath(startKey, endKey)), null, {
    method: "GET",
  });
}

/**
 * Follow pagination until all blocks in the date window are loaded.
 *
 * @param {string} startKey YYYY-MM-DD inclusive
 * @param {string} endKey YYYY-MM-DD inclusive
 * @returns {Promise<Array<Object>>}
 */
export async function fetchAllScheduleBlocksInRange(startKey, endKey) {
  const all = [];
  let path = scheduleListUrl(scheduleBlocksRangePath(startKey, endKey));
  while (path) {
    const page = await apiRequest(path, null, { method: "GET" });
    all.push(...scheduleListResults(page));
    path = scheduleApiPathFromNext(page?.next);
  }
  return all;
}

/**
 * Date-keyed Schedule weather rows for the visible timeline range.
 *
 * @param {string} startKey YYYY-MM-DD inclusive
 * @param {string} endKey YYYY-MM-DD inclusive
 * @param {string} [location="San Francisco"]
 * @returns {Promise<Record<string, Object>>}
 */
export async function getScheduleWeatherInRange(
  startKey,
  endKey,
  location = "San Francisco"
) {
  return getPlanningWeatherInRange(startKey, endKey, location);
}

/**
 * Load Schedule weather for the visible range as split past/future requests.
 *
 * @param {string} startKey YYYY-MM-DD inclusive
 * @param {string} endKey YYYY-MM-DD inclusive
 * @param {(weatherByDate: Record<string, Object>) => void} onSegmentLoaded
 * @param {string} [location="San Francisco"]
 * @returns {Promise<void>}
 */
export async function loadScheduleWeatherSplitByDay(
  startKey,
  endKey,
  onSegmentLoaded,
  location = "San Francisco"
) {
  return loadPlanningWeatherSplitByDay(startKey, endKey, onSegmentLoaded, location);
}

/**
 * Paginated scope blocks with nested aspects (`{ count, next, previous, results }`).
 * @deprecated Prefer {@link fetchAllScheduleBlocksInRange} for Schedule page loads.
 * @returns {Promise<{ count: number, next: string|null, previous: string|null, results: Array<Object> }>}
 */
export async function getScheduleBlocks() {
  return apiRequest(scheduleListUrl(API_PATHS.SCHEDULE.BLOCKS), null, {
    method: "GET",
  });
}

/**
 * Create a schedule group under a job (and optional parent).
 * @param {Object} body POST body (`job_id`, `name`, optional `parent_id`, `color`, `sort_order`).
 * @returns {Promise<Object>} Created group serializer shape.
 */
export async function createScheduleGroup(body) {
  return apiRequest(API_PATHS.SCHEDULE.GROUPS, body, { method: "POST" });
}

/** DELETE schedule group. Blocks under the group are retained by the backend. */
export async function deleteScheduleGroup(groupId) {
  return apiRequest(`${API_PATHS.SCHEDULE.GROUPS}${groupId}/`, null, {
    method: "DELETE",
  });
}

/**
 * PATCH schedule group (name, color, parent, sort order, etc.).
 * @param {string|number} groupId
 * @param {Object} body Partial group fields accepted by the API.
 * @returns {Promise<Object>} Updated group serializer shape.
 */
export async function patchScheduleGroup(groupId, body) {
  return apiRequest(`${API_PATHS.SCHEDULE.GROUPS}${groupId}/`, body, {
    method: "PATCH",
  });
}

/**
 * PATCH block (partial). Body may include `start_date`, `end_date`, `schedule_group_id`
 * (null for project row), `job_id` (target job when moving the block to another project),
 * and `work_scope_id` (catalog scope change).
 * Response is read shape with nested `aspects`.
 */
export async function patchScheduleBlock(blockId, body) {
  return patchPlanningScopeBlock(blockId, body);
}

/**
 * DELETE schedule block.
 * @param {string|number} blockId
 */
export async function deleteScheduleBlock(blockId) {
  return apiRequest(`${API_PATHS.SCHEDULE.BLOCKS}${blockId}/?delete_dispatch_data=true`, null, {
    method: "DELETE",
  });
}

/**
 * Set block.worker_assignment and all linked aspects to the same crew (read shape response).
 * @param {string|number} blockId
 * @param {number[]} workerIds
 */
export async function postScheduleBlockApplyWorkers(blockId, workerIds) {
  return apiRequest(
    `${API_PATHS.SCHEDULE.BLOCKS}${blockId}/apply_workers/`,
    { worker_ids: workerIds },
    { method: "POST" }
  );
}
